class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // Delegation: #CartDrawer innerHTML is replaced on refresh (e.g. bundle remove) — the overlay node
    // is recreated; per-element listeners from the constructor would be lost. Host listener survives swaps.
    this.addEventListener('click', (evt) => {
      const overlay = evt.target.closest('#CartDrawer-Overlay');
      if (!overlay || !this.contains(overlay)) return;
      evt.preventDefault();
      this.close();
    });
    this.setHeaderCartIconAccessibility();

    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      const params = new URLSearchParams(window.location.search);
      if (params.get('open_cart') === 'true') return;
      try {
        if (sessionStorage.getItem('ambrosiaOpenCart') === '1') return;
      } catch (e) {}
      if (this.classList.contains('active')) this.close();
    });
  }

  shouldSkipDrawerOpen() {
    if (window.AmbrosiaFastCheckout && typeof window.AmbrosiaFastCheckout.isRedirecting === 'function') {
      return window.AmbrosiaFastCheckout.isRedirecting();
    }
    return Number(window.__oseddCheckoutRedirectUntil || 0) > Date.now();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  /**
   * Lets bundle-builder-cart.js (and similar) run after the drawer DOM is in a known state.
   * Dawn uses <cart-drawer class="active"> — do not rely on inner #CartDrawer class hacks.
   *
   * When `cartData` is supplied (the already-fetched /cart.js JSON), it is passed in the
   * event detail so bundle-builder-cart.js can skip its own debounce + second /cart.js fetch
   * and run groupBundleItems synchronously — before the drawer opens.
   */
  notifyBundleBuilderCartSync(reason, cartData) {
    document.dispatchEvent(
      new CustomEvent('cart-drawer:bundle-sync', {
        bubbles: true,
        detail: {
          reason: reason || '',
          cartItems: (cartData && cartData.items) || null,
        },
      })
    );
  }

  /**
   * Section HTML only replaces `#CartDrawer` — Liquid cannot update `<cart-drawer class="is-empty">`.
   * Returns the full cart JSON so callers can pass it to notifyBundleBuilderCartSync, letting
   * bundle-builder-cart.js skip its own duplicate /cart.js fetch.
   */
  syncDrawerEmptyClassFromCart() {
    return fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        const empty = !(cart && cart.item_count);
        this.classList.toggle('is-empty', empty);
        const itemsHost = this.querySelector('cart-drawer-items');
        if (itemsHost) itemsHost.classList.toggle('is-empty', empty);
        if (empty) this.scheduleRecommendationsSwiperInit();
        return cart; // pass cart downstream for bundle-builder fast path
      })
      .catch(() => null);
  }

  scheduleRecommendationsSwiperInit() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.initializeRecommendationsSwiper());
    });
  }

  open(triggeredBy) {
    if (this.shouldSkipDrawerOpen()) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // Double rAF so the browser paints the closed state before we add .active
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('animate', 'active');
        this.scheduleRecommendationsSwiperInit();
      });
    });

    this.addEventListener(
      'transitionend',
      () => {
        const emptyContainer = this.querySelector('.drawer__inner-empty');
        const defaultContainer = document.getElementById('CartDrawer') || this.querySelector('.drawer__inner') || this;
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? (emptyContainer || defaultContainer)
          : defaultContainer;
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close') || containerToTrapFocusOn;
        trapFocus(containerToTrapFocusOn, focusElement);
        this.scheduleRecommendationsSwiperInit();
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.notifyBundleBuilderCartSync('open');
  }

  initializeRecommendationsSwiper() {
    // Initialize Swiper for any recommendations that are already in the DOM
    // (e.g., server-rendered empty cart recommendations). Nav buttons live in the
    // parent .cart-drawer__recommendations row, not inside .cart-drawer-recommendations-swiper.
    if (typeof Swiper === 'undefined') {
      this._swiperRetryCount = (this._swiperRetryCount || 0) + 1;
      if (this._swiperRetryCount <= 25) {
        setTimeout(() => this.initializeRecommendationsSwiper(), 100);
      }
      return;
    }
    this._swiperRetryCount = 0;

    const wrappers = this.querySelectorAll('.cart-drawer-recommendations-swiper');
    wrappers.forEach((swiperWrapper) => {
      const recommendationsRoot =
        swiperWrapper.closest('[data-cart-recommendations-content]') || swiperWrapper.closest('.cart-drawer__recommendations');
      const swiperContainer = swiperWrapper.querySelector('.swiper');
      if (!swiperContainer || !recommendationsRoot) return;

      // cart-recommendations.js may have already initialized this container
      if (swiperContainer.swiper || swiperWrapper.swiperInstance) {
        this.revealRecommendationsSwiper(swiperWrapper);
        return;
      }

      const visibleSlides = swiperContainer.querySelectorAll('.swiper-slide:not(.is-in-cart)');
      if (visibleSlides.length === 0) return;

      const instance = new Swiper(swiperContainer, {
        slidesPerView: 2,
        spaceBetween: 16,
        loop: false,
        watchOverflow: true,
        breakpoints: {
          750: {
            slidesPerView: 3,
            spaceBetween: 20,
          },
        },
        navigation: {
          nextEl: recommendationsRoot.querySelector('.swiper-button-next'),
          prevEl: recommendationsRoot.querySelector('.swiper-button-prev'),
        },
        pagination: {
          el: recommendationsRoot.querySelector('.swiper-pagination'),
          clickable: true,
          type: 'bullets',
        },
      });
      swiperWrapper.swiperInstance = instance;
      this.revealRecommendationsSwiper(swiperWrapper);
    });
  }

  revealRecommendationsSwiper(swiperWrapper) {
    if (!swiperWrapper) return;
    requestAnimationFrame(() => {
      swiperWrapper.classList.add('is-ready');
    });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    window.clearTimeout(this._closeOverflowTimer);
    this._closeOverflowTimer = window.setTimeout(() => {
      if (!this.classList.contains('active')) {
        document.body.classList.remove('overflow-hidden');
      }
    }, 450);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (this.shouldSkipDrawerOpen()) return Promise.resolve();
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    return this.syncDrawerEmptyClassFromCart().then((cart) => {
      // Pass the already-fetched cart JSON so bundle-builder-cart.js can run
      // groupBundleItems synchronously here — before setTimeout(() => open()) fires.
      // This eliminates the 60 ms debounce + second /cart.js round-trip that caused
      // raw line items to flash before the bundle group card appeared.
      this.notifyBundleBuilderCartSync('renderContents', cart);
      // Section HTML was replaced; do not rely on open() transitionend (may not fire if already open).
      this.scheduleRecommendationsSwiperInit();
      setTimeout(() => {
        this.open();
      });
    });
  }

  hasRequiredSections(parsedState) {
    if (!parsedState || !parsedState.sections) return false;
    const sections = this.getSectionsToRender();
    for (let i = 0; i < sections.length; i++) {
      if (!parsedState.sections[sections[i].id]) return false;
    }
    return true;
  }

  renderOrRefresh(parsedState, options = {}) {
    if (this.shouldSkipDrawerOpen()) return Promise.resolve(parsedState);
    const openDrawer = options.openDrawer !== false;
    const productId = options.productId || (parsedState && parsedState.id) || null;

    if (this.hasRequiredSections(parsedState)) {
      return Promise.resolve(this.renderContents(parsedState)).then(() => parsedState);
    }

    return this.refreshSections({ openDrawer, productId });
  }

  refreshSections(options = {}) {
    if (this.shouldSkipDrawerOpen()) return Promise.resolve();
    const openDrawer = options.openDrawer !== false;
    const productId = options.productId || null;
    const sectionIds = this.getSectionsToRender()
      .map((section) => section.id)
      .join(',');

    return fetch(`/?sections=${encodeURIComponent(sectionIds)}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Failed cart section refresh: ${response.status}`);
        return response.json();
      })
      .then((sections) => {
        const payload = { id: productId, sections: sections || {} };
        if (openDrawer) {
          return Promise.resolve(this.renderContents(payload)).then(() => payload);
        }
        // Keep this branch for non-opening refresh (e.g. bundle removed via /cart/update.js).
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);
          if (!sectionElement || !payload.sections[section.id]) return;
          sectionElement.innerHTML = this.getSectionInnerHTML(payload.sections[section.id], section.selector);
        });

        // The drawer is already open; immediately cloak the freshly-rendered cart items
        // so raw Shopify line-item rows are never painted while we wait for the /cart.js
        // fetch below. bundle-builder-cart.js's groupBundleItems will call uncloak() once
        // the group card is rebuilt and the rows are re-hidden. Safety timer is a fallback.
        const newItemsEl = document.querySelector('#CartDrawer-CartItems');
        if (newItemsEl) {
          newItemsEl.classList.add('bb-cloaked');
          setTimeout(() => newItemsEl.classList.remove('bb-cloaked'), 1500);
        }

        // Fire the bundle sync AFTER /cart.js resolves so we can pass the cart items
        // directly — the same fast path used by renderContents. This skips the 60 ms
        // debounce + duplicate /cart.js fetch that previously caused the raw-items flash.
        return this.syncDrawerEmptyClassFromCart().then((cart) => {
          this.notifyBundleBuilderCartSync('refreshSections', cart);
          this.scheduleRecommendationsSwiperInit();
          return payload;
        });
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
